/**
 * Validates that a redirect target is safe (same-origin, path-relative).
 *
 * Mirrors the sw.js convention at apps/web/public/sw.js
 * (regression-tested at apps/web/src/__tests__/sw-push-handler.test.ts:137-149
 * for the `//evil.com` protocol-relative attack class).
 *
 *   - `/setup/accept?token=abc`  → ALLOWED (same-origin path)
 *   - `//evil.com/x`             → REJECTED (protocol-relative)
 *   - `/\evil.com/x`             → REJECTED (backslash variant — Chromium
 *                                  history.pushState normalises `\` to `/`
 *                                  so `/\evil.com` becomes the
 *                                  protocol-relative attack)
 *   - `https://evil.com/x`       → REJECTED (no leading `/`)
 *   - `setup/accept`             → REJECTED (no leading `/`)
 *
 * Extracted from inline LoginPage.tsx in ONB.B2-FIX.D2 (#TBD). The original
 * 4-line check now lives here as a type-predicate so callers narrow `value`
 * to `string` after the check passes.
 *
 * Frontend is one layer of defense; the backend MUST apply an equivalent
 * server-side guard at any handler that consumes a redirect query param
 * (see apps/api/src/routes/auth.ts /auth/register POST and
 * /auth/verify-email GET).
 */
export function isSafeRedirect(
  value: string | null | undefined,
): value is string {
  return Boolean(
    value &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.startsWith('/\\') &&
    // #1975 sibling-sweep — the WHATWG URL parser DELETES every ASCII tab, LF
    // and CR from its input before parsing, and that happens after the
    // leading-character checks above have run. So `/\t/evil.com/x` passes
    // them, then resolves as the protocol-relative `//evil.com/x` — origin
    // evil.com. The prefix checks above inspect a string the parser will never
    // actually see. Rejecting the three strippable characters is what makes
    // this predicate mean what its name claims.
    //
    // Latent rather than live at the time it was found: every current caller
    // hands the value to react-router `navigate()`, whose pushState throws a
    // SecurityError on a cross-origin URL instead of following it. A future
    // caller assigning to window.location would have made it a live open
    // redirect.
    !/[\t\n\r]/.test(value),
  );
}
